import util from "../../lib/util";
import store from "../../store/store";
import {InterestMethods, LeadPeriodChoices, RoundingMethods} from './OptionConstants';
import * as OptionActions from '../../store/actions/optionActions';
import Option from '../../entities/option';
import {deleteOption} from '../../store/deps/optionDeps';
import {getCurrentFirmId, getCurrentUserId} from "../../lib/picos/currentLogin";
import {genOptionUUID} from "../../lib/picos/uuids";

class OptionHelper {
    // Constants for option names
    static BILLING_INCREMENT = 'billingIncrement';
    static EMAIL_CLIENT = 'paymentEmailClient';
    static EMAIL_PAYER = 'paymentEmailPayer';
    static EMAIL_SELF = 'paymentEmailSelf';
    static FONT_FAMILY = 'invoiceFontFamily';
    static FONT_SIZE = 'invoiceFontSize';
    static FIRM_ADDRESS = 'invoiceFirmAddress';
    static FIRM_LETTERHEAD = 'invoiceFirmLetterhead';
    static LETTERHEAD_WIDTH = 'invoiceLetterheadWidth';
    static PAID_CONSULTS = 'paidConsults';
    static PAID_CONSULT_PRICE = 'paidConsultPrice';
    static PAID_CONSULT_DURATION = 'paidConsultDuration';
    static PAID_CONSULT_NAME = 'paidConsultName';
    static PAID_CONSULT_ACTIVITY = 'paidConsultActivityId';
    static LEAD_FOLLOWUP_ACTIVITY = 'leadFollowupActivityId';
    static LEAD_INITIAL_CONSULT_USER = 'leadInitialConsultUser';
    static USE_CASE_TRUST = 'useCaseTrust';
    static INVOICE_DEFAULT_SUMMARY = 'invoiceDefaultSummary';
    static INVOICE_NARRATIVE_HEADER = 'invoiceNarrativeHeader';
    static INVOICE_TERMS = 'invoiceTerms';
    static ENFORCE_ACTIVITY_TYPE = 'enforceActivityType';
    static ENFORCE_CATEGORY_SELECTED = 'enforceCategorySelected';
    static ENFORCE_TIE_INS_COMPLETE = 'enforceTieInsComplete';
    static ROUNDING_METHOD = 'roundingMethod';
    static SHOW_HELP = 'showHelp';
    static SOUND_FOR_WIN = 'soundForWin';
    static SOUND_FOR_TASK_COMPLETE = 'soundForTaskComplete';
    static TASK_EMOJIS = 'taskEmojis';

    /*** Tax & Interest ***/
    static INVOICE_TAX_RATE = 'invoiceTaxRate';
    static INVOICE_TAX_LABEL = 'invoiceTaxLabel';
    static INVOICE_INTEREST_RATE = 'invoiceInterestRate';
    static INVOICE_INTEREST_METHOD = 'invoiceInterestMethod';
    static INVOICE_INTEREST_GRACE_PERIOD = 'invoiceInterestGracePeriod';
    static INVOICE_LEAD_PERIOD = 'invoiceLeadPeriod';

    /*** Payment Portal ***/
    static PAYMENT_PORTAL = 'paymentPortal';
    static PAYMENT_PORTAL_BENEFACTORS_LINK = 'paymentPortalBenefactorsLink';
    static PAYMENT_PORTAL_BENEFACTORS_INVOICE = 'paymentPortalBenefactorsInvoice';

    static getDefaultsForLabel = (label: string): {defaultValue: any, type: string} => {
        switch (label) {
            case this.BILLING_INCREMENT:
                return {defaultValue: 1, type: 'numeric'};
            case this.EMAIL_CLIENT:
            case this.EMAIL_PAYER:
            case this.EMAIL_SELF:
                return {defaultValue: true, type: 'boolean'};
            case this.PAYMENT_PORTAL_BENEFACTORS_LINK:
                return {defaultValue: true, type: 'boolean'};
            case this.FONT_FAMILY:
                return {defaultValue: 'Open Sans, sans-serif', type: 'string'};
            case this.FONT_SIZE:
                return {defaultValue: '10pt', type: 'string'};
            case this.FIRM_ADDRESS:
                return {defaultValue: null, type: 'string'};
            case this.FIRM_LETTERHEAD:
                return {defaultValue: 'Set up letterhead in Options/Invoicing', type: 'string'};
            case this.LETTERHEAD_WIDTH:
                // No default: until a firm sets a width, the image keeps its own size.
                return {defaultValue: null, type: 'numeric'};
            case this.PAID_CONSULTS:
                return {defaultValue: true, type: 'boolean'};
            case this.PAID_CONSULT_PRICE:
                return {defaultValue: 250, type: 'numeric'};
            case this.PAID_CONSULT_DURATION:
                return {defaultValue: 1, type: 'numeric'};
            case this.PAID_CONSULT_NAME:
                return {defaultValue: 'Initial Consultation', type: 'string'};
            case this.PAID_CONSULT_ACTIVITY:
                return {defaultValue: 'aea93c81-c896-4116-82f0-99864deef232', type: 'string'};
            case this.LEAD_FOLLOWUP_ACTIVITY:
                return {defaultValue: '7a87b303-ce41-428e-98b2-bfdca74d2a83', type: 'string'};
            case this.LEAD_INITIAL_CONSULT_USER:
                return {defaultValue: '', type: 'string'};
            case this.USE_CASE_TRUST:
                return {defaultValue: true, type: 'boolean'};
            case this.INVOICE_DEFAULT_SUMMARY:
                return {defaultValue: '', type: 'string'};
            case this.INVOICE_NARRATIVE_HEADER:
                return {defaultValue: 'Work We Did for You', type: 'string'};
            case this.INVOICE_TERMS:
                return {defaultValue: 0, type: 'numeric'};
            case this.ENFORCE_ACTIVITY_TYPE:
                return {defaultValue: false, type: 'boolean'};
            case this.ENFORCE_CATEGORY_SELECTED:
                return {defaultValue: false, type: 'boolean'};
            case this.ENFORCE_TIE_INS_COMPLETE:
                return {defaultValue: true, type: 'boolean'};
            case this.TASK_EMOJIS:
                return {defaultValue: false, type: 'boolean'};
            case this.ROUNDING_METHOD:
                return {defaultValue: RoundingMethods.ROUNDING_METHOD_MIDDLE, type: 'string'};
            case this.SHOW_HELP:
                return {defaultValue: true, type: 'boolean'};
            case this.SOUND_FOR_TASK_COMPLETE:
                return {defaultValue: true, type: 'boolean'};
            case this.SOUND_FOR_WIN:
                return {defaultValue: true, type: 'boolean'};
            case this.PAYMENT_PORTAL:
                return {defaultValue: true, type: 'boolean'};
            case this.PAYMENT_PORTAL_BENEFACTORS_INVOICE:
                return {defaultValue: false, type: 'boolean'};
            case this.INVOICE_TAX_RATE:
                return {defaultValue: 0, type: 'numeric'};
            case this.INVOICE_TAX_LABEL:
                return {defaultValue: '', type: 'string'};
            case this.INVOICE_INTEREST_RATE:
                return {defaultValue: 0, type: 'numeric'};
            case this.INVOICE_INTEREST_METHOD:
                return {defaultValue: InterestMethods.SIMPLE_MONTHLY, type: 'string'};
            case this.INVOICE_INTEREST_GRACE_PERIOD:
                return {defaultValue: 0, type: 'numeric'};
            case this.INVOICE_LEAD_PERIOD:
                return {defaultValue: LeadPeriodChoices.UP_TO_CONSULT, type: 'string'};
            default:
                return {defaultValue: '', type: 'string'}
        }
    }

    /**
     * Get an option based on its label.
     *
     * @param {string} label
     * @return {Option}
     */
    static getOptionByLabel = (label: string): Option => {
        // Get defaults for this label
        const {defaultValue, type} = OptionHelper.getDefaultsForLabel(label);

        // See if the option already exists
        const result = util.sortEntityArrayByDate(store.getState().options
            .getMappingFor('label', label), 'addedOn', 'desc')[0];

        if (!result) {
            // See if it exists in defaultEntries section
            return Option.getDefaultEntries().find(o => o.label === label) || new Option({
                optionId: genOptionUUID(getCurrentFirmId(), label),
                firmId: getCurrentFirmId(),
                addedBy: getCurrentUserId(),
                addedOn: 0.1,
                status: 'C',
                userScope: false,
                label,
                type,
                value: (defaultValue != null)
                       ? type === 'string'
                         ? defaultValue
                         : type === 'boolean'
                           ? defaultValue === true || defaultValue === 'true' || defaultValue === 't'
                           : `${defaultValue}`
                       : null,

            });
        }
        else return result;
    }

    getOptionByLabel = (label: string) => {
        return OptionHelper.getOptionByLabel(label);
    }

    /**
     * Get the value of the option.
     *
     * @param {string} label
     * @return {string|number|boolean|null}
     */
    getOptionValue = <T = any>(label: string): T => {
        try {
            const option = this.getOptionByLabel(label);
            const value = option.value;

            return (option.type === 'numeric'
                   ? Number.parseFloat(value)
                   : option.type === 'boolean'
                     ? typeof value === 'string'
                       ? value.toLowerCase().startsWith('t')
                       : value
                     : value) as T;
        }
        catch (err: any) {
            console.log(err.message);
            return null;
        }
    }

    /**
     * Static version (optimized) of getOptionValue.
     *
     * @param {string} label
     * @return {string|number|boolean|null}
     */
    static getOptionValueStatic = (label: string): string | number | boolean | null => {
        const option = this.getOptionByLabel(label);
        const value = option.value;

        try {
            if (option.type === 'numeric') {
                try {
                    return Number.parseFloat(value)
                }
                catch (err: any) {
                    console.warn(err.message);
                    return null;
                }
            }
            else if (option.type === 'boolean') {
                if (typeof value === 'string') {
                    try {
                        return value.toLowerCase().startsWith('t')
                    }
                    catch (err: any) {
                        console.warn(err.message);
                        return null;
                    }
                }
                else return value;
            }
            else return value;
        }
        catch (err: any) {
            console.log(err.message);
            return null;
        }
    }

    /**
     * Set an option by label/value and optional type.
     * @param {string} label
     * @param {string|number|boolean} value
     * @param {string} [type]
     * @returns {Option}
     */
    setOption = (label: string, value: string | number | boolean, type: string = 'string'): Option => {
        // First see if the option exists already
        let option: Option;

        try {
            option = this.getOptionByLabel(label);
        }
        catch (err) {
            // Safe to ignore
        }

        if (!option) option = new Option({
            firmId: getCurrentFirmId(),
            optionId: genOptionUUID(getCurrentFirmId(), label),
            addedBy: getCurrentUserId(),
            addedOn: util.newHRTimestamp(),
            type, label, value
        });
        else {
            // Save a new version; never change the stored option before its write lands
            option = option.clone();
            option.addedBy = getCurrentUserId();
            option.addedOn = util.newHRTimestamp();
            option.value = `${value}`;
        }

        // Now save it, as a plain save even when cloned from a queued restore. The server appends
        // after a delete of the same ID (OWA-1294).
        option.restore = false;
        store.dispatch(OptionActions.saveOptionQueue(option));

        return option;
    }

    /**
     * Delete an option in its entirety.
     *
     * @param {string} label
     */
    deleteOptionByLabel = (label: string) => {
        const option = this.getOptionByLabel(label);

        if (option) deleteOption(option);
    }
}

export default OptionHelper;
